import { BillingCustomerContext } from 'context/billing-customer.context';
import type { IApiError } from 'models';
import { useContext, useEffect, useState } from 'react';

interface UseApiStateOptions<P extends unknown[]> {
  eager?: boolean;
  dependencies?: P;
}

type ApiResult<T> = T extends void ? void : T extends boolean ? boolean : T | null;

export function useApiState<T, P extends unknown[]>(
  apiCall: (bcid: string, ...params: P) => Promise<T | void>,
  options?: UseApiStateOptions<P>
) {
  const { activeBcId } = useContext(BillingCustomerContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<IApiError | null>(null);
  const [data, setData] = useState<T | null>(null);

  const execute = async (...params: P): Promise<ApiResult<T>> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiCall(activeBcId, ...params);
      if (result !== undefined) {
        setData(result as T);
        return result as ApiResult<T>;
      }
      return undefined as ApiResult<T>;
    } catch (err) {
      setError(err);
      setData(null);
      return null as ApiResult<T>;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (options?.eager) {
      execute(...(options.dependencies || ([] as unknown as P)));
    }
  }, [options?.dependencies]);

  return { data, isLoading, error, execute };
}

export function useUnauthenticatedApiState<T, P extends unknown[]>(
  apiCall: (...params: P) => Promise<T | void>,
  options?: UseApiStateOptions<P>
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<IApiError | null>(null);
  const [data, setData] = useState<T | null>(null);

  const execute = async (...params: P): Promise<ApiResult<T>> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiCall(...params);
      if (result !== undefined) {
        setData(result as T);
        return result as ApiResult<T>;
      }
      return undefined as ApiResult<T>;
    } catch (err) {
      setError(err);
      setData(null);
      return null as ApiResult<T>;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (options?.eager) {
      execute(...(options.dependencies || ([] as unknown as P)));
    }
  }, [options?.dependencies]);

  return { data, isLoading, error, execute };
}
